import React, { useState, useEffect, useMemo, useCallback, useRef, memo } from 'react'
import {
  Modal,
  Input,
  Button,
  message,
  Tag,
  Spin,
  Space,
  Row,
  Col,
  Form,
  Select,
  Switch,
  Tooltip,
} from 'antd'
import { SearchOutlined, SettingOutlined } from '@ant-design/icons'
import type { CommonBindModalProps, BindItem, SearchConfig } from './types'
import VirtualList from './VirtualList'
import PerformanceMonitor from './PerformanceMonitor'
import './index.less'

const { TextArea } = Input

// 虚拟滚动阈值
const VIRTUAL_SCROLL_THRESHOLD = 100

// 使用React.memo包装组件，避免不必要的重渲染
const CommonBindModal: React.FC<CommonBindModalProps> = memo(({
  visible,
  selectionMode,
  onVisibleChange,
  title,
  icon,
  itemHeight = 110,
  width = 800,
  height = 600,
  searchConfig,
  onSearch,
  onBind,
  onBindSuccess,
  onCancel,
  searchPlaceholder = '请输入搜索关键词',
  emptyText = '暂无搜索结果',
  selectEmptyText = '请先选择要绑定的项目',
  loadingText = '加载中...',
  searchLoadingText = '搜索中...',
  bindLoadingText = '绑定中...',
  confirmText = '确认绑定',
  cancelText = '取消',
  searchText = '搜索',
  selectAllText = '全选',
  unselectAllText = '取消全选',
  removeText = '移除',
  currentBoundText = '当前已绑定',
  currentBoundTagColor = 'orange',
  selectedTagColor = 'orange',
  boundTagColor = 'blue',
  unboundTagColor = 'default',
  selectedBorderColor = '#f7942a',
  currentBoundBorderColor = '#fa8c16',
  selectedBgColor = '#fff7e6',
  currentBoundBgColor = '#fff7e6',
  currentBoundShadow = '0 2px 8px rgba(250, 140, 22, 0.15)',
  showSelectAll = true,
  showForm = false,
  formConfig,
  renderCustomContent,
  renderCustomItem,
  renderCustomSelectedItem,
  renderCustomForm,
  renderCustomEmpty,
  renderCustomSelectedEmpty,
  className,
  style,
}) => {
  const [form] = Form.useForm()
  
  // 性能优化开关
  const [enablePerformanceOptimizations, setEnablePerformanceOptimizations] = useState(true)
  const [showPerformanceMonitor, setShowPerformanceMonitor] = useState(false)
  
  // 使用useRef来避免不必要的状态更新
  const stateRef = useRef({
    loading: false,
    searchLoading: false,
    bindLoading: false,
    selectedItemsLoading: false,
  })
  
  const [loading, setLoading] = useState(false)
  const [searchLoading, setSearchLoading] = useState(false)
  const [bindLoading, setBindLoading] = useState(false)
  const [allItems, setAllItems] = useState<BindItem[]>([])
  const [filteredItems, setFilteredItems] = useState<BindItem[]>([])
  const [searchKeyword, setSearchKeyword] = useState('')
  const [currentBoundItems, setCurrentBoundItems] = useState<BindItem[]>([])
  const [selectedItem, setSelectedItem] = useState<BindItem | null>(null)
  const [selectedItems, setSelectedItems] = useState<BindItem[]>([])
  
  // 使用ref来避免重复调用和内存泄漏
  const fetchTimerRef = useRef<NodeJS.Timeout | null>(null)
  const isMountedRef = useRef(true)
  const dataCacheRef = useRef<Map<string, BindItem[]>>(new Map())
  const lastFetchTimeRef = useRef<number>(0)
  const CACHE_EXPIRE_TIME = 30000 // 30秒缓存过期

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      isMountedRef.current = false
      if (fetchTimerRef.current) {
        clearTimeout(fetchTimerRef.current)
      }
      // 清理缓存
      dataCacheRef.current.clear()
    }
  }, [])

  // 是否为单选模式 - 使用useMemo优化
  const isSingleSelect = useMemo(() => {
    return !showSelectAll || selectionMode === 'single'
  }, [showSelectAll, selectionMode])

  // 是否启用虚拟滚动 - 使用useMemo优化
  const enableVirtualScroll = useMemo(() => {
    return enablePerformanceOptimizations && filteredItems.length > VIRTUAL_SCROLL_THRESHOLD
  }, [enablePerformanceOptimizations, filteredItems.length])

  // 是否启用已选项目虚拟滚动 - 使用useMemo优化
  const enableSelectedVirtualScroll = useMemo(() => {
    return enablePerformanceOptimizations && selectedItems.length > VIRTUAL_SCROLL_THRESHOLD
  }, [enablePerformanceOptimizations, selectedItems.length])

  // 缓存搜索结果 - 使用useMemo优化
  const cachedFilteredItems = useMemo(() => {
    if (!enablePerformanceOptimizations) return allItems
    
    if (searchKeyword.trim() && searchConfig?.filterFunction) {
      return allItems.filter(item => searchConfig.filterFunction!(item, searchKeyword))
    }
    return allItems
  }, [enablePerformanceOptimizations, searchKeyword, allItems, searchConfig?.filterFunction])

  // 更新filteredItems - 使用useEffect优化
  useEffect(() => {
    setFilteredItems(cachedFilteredItems)
  }, [cachedFilteredItems])

  // 获取所有项目列表 - 使用useCallback优化
  const fetchAllItems = useCallback(async () => {
    if (!searchConfig?.fetchFunction || stateRef.current.searchLoading || !isMountedRef.current) return

    // 检查缓存
    const cacheKey = `all_${searchKeyword}`
    const now = Date.now()
    const cachedData = dataCacheRef.current.get(cacheKey)
    
    if (enablePerformanceOptimizations && cachedData && (now - lastFetchTimeRef.current) < CACHE_EXPIRE_TIME) {
      console.log('使用缓存数据')
      setAllItems(cachedData)
      setFilteredItems(cachedData)
      return
    }

    try {
      stateRef.current.searchLoading = true
      setSearchLoading(true)
      
      const response = await searchConfig.fetchFunction(searchKeyword)
      
      if (isMountedRef.current) {
        if (response?.ret && response?.data) {
          const items = Array.isArray(response.data) ? response.data : response.data.list || []
          
          // 缓存数据
          if (enablePerformanceOptimizations) {
            dataCacheRef.current.set(cacheKey, items)
            lastFetchTimeRef.current = now
          }
          
          setAllItems(items)
          setFilteredItems(items)
        } else {
          console.warn('获取数据失败:', response?.msg || '响应数据格式错误')
          setAllItems([])
          setFilteredItems([])
        }
      }
    } catch (error) {
      if (isMountedRef.current) {
        console.error('获取数据失败:', error)
        message.error('获取数据失败，请稍后重试')
        setAllItems([])
        setFilteredItems([])
      }
    } finally {
      if (isMountedRef.current) {
        stateRef.current.searchLoading = false
        setSearchLoading(false)
      }
    }
  }, [searchConfig?.fetchFunction, searchKeyword, enablePerformanceOptimizations])

  // 当弹窗打开时，如果有fetchSelectedItemsFunction，获取已选项目数据
  useEffect(() => {
    if (visible && searchConfig?.fetchSelectedItemsFunction) {
      console.log('弹窗打开，开始获取已选项目数据...')
      fetchSelectedItemsFromApi()
    }
  }, [visible, searchConfig?.fetchSelectedItemsFunction])

  // 从接口获取已选项目数据 - 使用useCallback优化
  const fetchSelectedItemsFromApi = useCallback(async () => {
    if (!searchConfig?.fetchSelectedItemsFunction || !isMountedRef.current) {
      return
    }

    try {
      stateRef.current.selectedItemsLoading = true
      setSearchLoading(true)
      
      const response = await searchConfig.fetchSelectedItemsFunction([])
      
      if (isMountedRef.current && response?.ret && response?.data) {
        const items = Array.isArray(response.data) ? response.data : response.data.list || []
        setSelectedItems(items)
        if(selectionMode === 'single' && items.length > 0) {
          setSelectedItem(items[0] || {} as BindItem)
        }
        console.log('从接口获取到的已选项目:', items, '已选ID:', items.map((item: BindItem) => item.id))
      } else {
        console.warn('获取已选项目数据失败:', response?.msg || '响应数据格式错误')
        setSelectedItems([])
        setSelectedItem(null)
      }
    } catch (error) {
      if (isMountedRef.current) {
        console.error('获取已选项目数据失败:', error)
        setSelectedItems([])
        setSelectedItem(null)
      }
    } finally {
      if (isMountedRef.current) {
        stateRef.current.selectedItemsLoading = false
        setSearchLoading(false)
      }
    }
  }, [searchConfig?.fetchSelectedItemsFunction])

  // 获取所有项目列表 - 优化后的useEffect
  useEffect(() => {
    if (visible && searchConfig?.fetchOnMount !== false) {
      console.log('弹窗打开，开始获取数据...')
      // 清理之前的定时器
      if (fetchTimerRef.current) {
        clearTimeout(fetchTimerRef.current)
      }
      
      // 使用ref来避免重复调用
      fetchTimerRef.current = setTimeout(() => {
        if (isMountedRef.current) {
          console.log('延迟获取数据...')
          fetchAllItems()
        }
      }, enablePerformanceOptimizations ? 100 : 0) // 根据性能优化开关调整延迟
    }
  }, [visible, fetchAllItems, enablePerformanceOptimizations]) // 添加fetchAllItems依赖

  // 处理搜索 - 使用useCallback优化
  const handleSearch = useCallback(() => {
    if (searchConfig?.fetchFunction) {
      fetchAllItems()
    } else if (searchConfig?.searchFunction) {
      handleCustomSearch()
    }
  }, [searchConfig?.fetchFunction, searchConfig?.searchFunction, fetchAllItems])

  // 自定义搜索 - 使用useCallback优化
  const handleCustomSearch = useCallback(async () => {
    if (!searchConfig?.searchFunction) return

    try {
      stateRef.current.searchLoading = true
      setSearchLoading(true)
      
      const response = await searchConfig.searchFunction(searchKeyword)
      
      if (response?.ret && response?.data) {
        const items = Array.isArray(response.data) ? response.data : response.data.list || []
        setFilteredItems(items)
        if (items.length === 0) {
          message.info('未找到相关数据')
        }
      } else {
        console.warn('搜索失败:', response?.msg || '响应数据格式错误')
        setFilteredItems([])
        message.error(response?.msg || '搜索失败')
      }
    } catch (error) {
      console.error('搜索失败:', error)
      message.error('搜索失败，请稍后重试')
      setFilteredItems([])
    } finally {
      setSearchLoading(false)
      stateRef.current.searchLoading = false
    }
  }, [searchConfig?.searchFunction, searchKeyword])

  // 选择/取消选择项目 - 使用useCallback优化
  const handleSelectItem = useCallback((itemId: number, checked: boolean) => {
    if (isSingleSelect) {
      // 单选模式
      if (checked) {
        const item = filteredItems.find(item => item.id === itemId)
        setSelectedItem(item || null)
        setSelectedItems([item || {} as BindItem])
      } else {
        setSelectedItem(null)
        setSelectedItems([])
      }
    } else {
      // 多选模式
      const item = filteredItems.find(item => item.id === itemId)
      if (checked) {
        setSelectedItems(prev => [...prev, item || {} as BindItem])
      } else {
        setSelectedItems(prev => prev.filter(item => item.id !== itemId))
      }
    }
  }, [isSingleSelect, filteredItems])

  // 全选/取消全选 - 使用useCallback优化
  const handleSelectAll = useCallback((checked: boolean) => {
    if (checked) {
      setSelectedItems(filteredItems)
    } else {
      setSelectedItems([])
    }
  }, [filteredItems])

  // 提交绑定 - 使用useCallback优化
  const handleSubmit = useCallback(async () => {
    if (selectedItems.length === 0) {
      message.warning('请至少选择一个项目')
      return
    }

    try {
      stateRef.current.loading = true
      setLoading(true)
      
      let bindData: any = { selectedIds: selectedItems.map(item => item.id) }
      
      // 如果有表单配置，获取表单数据
      if (showForm && formConfig) {
        const formValues = await form.validateFields()
        bindData = { ...bindData, ...formValues }
      }

      // 调用绑定函数
      if (onBind) {
        const success = await onBind(bindData)
        if (success) {
          message.success('绑定成功')
          handleSuccess()
        }
      } else if (searchConfig?.bindFunction) {
        const response = await searchConfig.bindFunction(bindData)
        if (response?.ret) {
          message.success('绑定成功')
          handleSuccess()
        } else {
          message.error(response?.msg || '绑定失败')
        }
      }
    } catch (error) {
      console.error('绑定失败:', error)
      message.error('绑定失败，请稍后重试')
    } finally {
      stateRef.current.loading = false
      setLoading(false)
    }
  }, [selectedItems, showForm, formConfig, onBind, searchConfig?.bindFunction])

  // 处理成功 - 使用useCallback优化
  const handleSuccess = useCallback(() => {
    onVisibleChange(false)
    setSearchKeyword('')
    // 清理缓存
    if (enablePerformanceOptimizations) {
      dataCacheRef.current.clear()
    }
    if (onBindSuccess) {
      onBindSuccess()
    }
  }, [onVisibleChange, onBindSuccess, enablePerformanceOptimizations])

  // 取消 - 使用useCallback优化
  const handleCancel = useCallback(() => {
    // 重置为初始状态
    setSelectedItems([])
    setSearchKeyword('')
    setSelectedItem(null)
    form.resetFields()
    // 清理缓存
    if (enablePerformanceOptimizations) {
      dataCacheRef.current.clear()
    }
    onVisibleChange(false)
    if (onCancel) {
      onCancel()
    }
  }, [form, onVisibleChange, onCancel, enablePerformanceOptimizations])

  // 渲染项目项 - 使用useCallback优化
  const renderItem = useCallback((item: BindItem, index: number) => {
    return renderCustomItem ? renderCustomItem(item, index, {
      isSelected: selectedItems.some(selected => selected.id === item.id),
      isCurrentBound: currentBoundItems.some(ci => ci.id === item.id),
      handleSelect: handleSelectItem
    }) : null
  }, [renderCustomItem, selectedItems, currentBoundItems, handleSelectItem])

  // 渲染已选项目 - 使用useCallback优化
  const renderSelectedItem = useCallback((item: BindItem, index: number) => {
    
    // 默认渲染已选项目，包含关闭按钮
    return (
      <div
        key={item.id}
        className="selected-item"
      >
        {/* 关闭按钮 - 悬浮在右上方 */}
        <div
          className="remove-btn"
          onClick={() => handleSelectItem(item.id!, false)}
        >
        </div>
        { renderCustomSelectedItem ? renderCustomSelectedItem(item, index, {
            isCurrentBound: currentBoundItems.some(ci => ci.id === item.id),
            handleRemove: () => handleSelectItem(item.id!, false)
          }):null
        }
      </div>
    )
  }, [renderCustomSelectedItem, currentBoundItems, handleSelectItem])

  // 渲染空状态 - 使用useCallback优化
  const renderEmpty = useCallback(() => {
    if (renderCustomEmpty) {
      return renderCustomEmpty()
    }

    return (
      <div className="empty-state">
        {emptyText}
      </div>
    )
  }, [renderCustomEmpty, emptyText])

  // 渲染已选空状态 - 使用useCallback优化
  const renderSelectedEmpty = useCallback(() => {
    if (renderCustomSelectedEmpty) {
      return renderCustomSelectedEmpty()
    }

    return (
      <div className="selected-empty-state">
        {icon && React.cloneElement(icon as React.ReactElement<any>, { 
          style: { fontSize: '48px', marginBottom: '16px', display: 'block' } 
        })}
        <div>{selectEmptyText}</div>
      </div>
    )
  }, [renderCustomSelectedEmpty, icon, selectEmptyText])

  // 渲染表单 - 使用useCallback优化
  const renderForm = useCallback(() => {
    if (renderCustomForm) {
      return renderCustomForm(form, selectedItem)
    }

    if (!showForm || !formConfig) return null

    return (
      <div className="bind-form">
        <div className="form-title">绑定信息</div>
        <Form form={form} layout="vertical">
          {formConfig.fields?.map(field => (
            <Form.Item
              key={field.name}
              name={field.name}
              label={field.label}
              rules={field.rules}
            >
              {field.render ? field.render() : (
                field.type === 'textarea' ? (
                  <TextArea rows={3} placeholder={field.placeholder} />
                ) : field.type === 'select' ? (
                  <Select placeholder={field.placeholder} options={field.options} />
                ) : (
                  <Input placeholder={field.placeholder} />
                )
              )}
            </Form.Item>
          ))}
        </Form>
      </div>
    )
  }, [renderCustomForm, form, selectedItem, showForm, formConfig])

  // 使用useMemo优化搜索结果数量显示
  const resultsCount = useMemo(() => filteredItems.length, [filteredItems])
  const selectedCount = useMemo(() => selectedItems.length, [selectedItems])

  // 使用useMemo优化弹窗标题
  const modalTitle = useMemo(() => (
    <div className="modal-title">
      {icon && React.cloneElement(icon as React.ReactElement<any>, { style: { marginRight: '8px' } })}
      {title}
    </div>
  ), [icon, title])

  // 使用useMemo优化弹窗底部按钮
  const modalFooter = useMemo(() => [
    <Button key="cancel" onClick={handleCancel}>
      {cancelText}
    </Button>,
    <Button
      key="submit"
      type="primary"
      loading={loading}
      onClick={handleSubmit}
      disabled={selectedCount === 0}
    >
      {confirmText} {!isSingleSelect && `(${selectedCount})`}
    </Button>,
  ], [handleCancel, cancelText, loading, handleSubmit, confirmText, isSingleSelect, selectedCount])

  // 渲染搜索结果列表
  const renderResultsList = () => {
    if (enableVirtualScroll) {
      return (
        <VirtualList
          items={filteredItems}
          itemHeight={itemHeight} // 根据实际项目高度调整
          containerHeight={height - 350}
          renderItem={renderItem}
          overscan={5}
          className="virtual-results-list"
        />
      )
    }

    return (
      <div className="results-list">
        {resultsCount > 0 ? (
          filteredItems.map((item, index) => renderItem(item, index))
        ) : (
          renderEmpty()
        )}
      </div>
    )
  }

  // 渲染已选项目列表
  const renderSelectedList = () => {
    if (enableSelectedVirtualScroll) {
      return (
        <VirtualList
          items={selectedItems}
          itemHeight={itemHeight} // 根据实际项目高度调整
          containerHeight={550}
          renderItem={renderSelectedItem}
          overscan={5}
          className="virtual-selected-list"
        />
      )
    }

    return (
      <div className="selected-list" style={{height: `${height - 250}px`, overflow: 'auto'}}>
        {selectedCount > 0 ? (
          selectedItems.map((item, index) => renderSelectedItem(item, index))
        ) : (
          renderSelectedEmpty()
        )}
      </div>
    )
  }

  return (
    <>
      <Modal
        title={modalTitle}
        open={visible}
        onCancel={handleCancel}
        width={width}
        height={height}
        destroyOnClose
        className={`common-bind-modal ${className || ''}`}
        style={style}
        footer={modalFooter}
      >
        <div className="modal-content" style={{ height: `${height - 200}px`, overflow: 'hidden' }}>
          {/* 左侧：搜索和选择 */}
          <div className="left-panel">
            <div className="search-section">
              <div className="section-title">
                {searchConfig?.title || '可选项目列表'}
              </div>
              
              {/* 性能优化控制面板 */}
              {/* <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: '12px',
                padding: '8px',
                background: '#fafafa',
                borderRadius: '4px',
                border: '1px solid #f0f0f0'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Tooltip title="启用性能优化功能">
                    <Switch
                      size="small"
                      checked={enablePerformanceOptimizations}
                      onChange={setEnablePerformanceOptimizations}
                      checkedChildren="优化开"
                      unCheckedChildren="优化关"
                    />
                  </Tooltip>
                  <span style={{ fontSize: '12px', color: '#666' }}>
                    性能优化
                  </span>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Tooltip title="显示性能监控面板">
                    <Button
                      size="small"
                      type={showPerformanceMonitor ? 'primary' : 'default'}
                      icon={<SettingOutlined />}
                      onClick={() => setShowPerformanceMonitor(!showPerformanceMonitor)}
                    >
                      监控
                    </Button>
                  </Tooltip>
                </div>
              </div> */}
              
              <Row gutter={8} className="search-row">
                <Col flex="auto">
                  <Input
                    placeholder={searchPlaceholder}
                    value={searchKeyword}
                    onChange={(e) => setSearchKeyword(e.target.value)}
                    onPressEnter={handleSearch}
                  />
                </Col>
                <Col>
                  <Button
                    type="primary"
                    icon={<SearchOutlined />}
                    loading={searchLoading}
                    onClick={handleSearch}
                  >
                    {searchText}
                  </Button>
                </Col>
              </Row>
              {showSelectAll && !isSingleSelect && (
                <Space className="select-actions">
                  <Button
                    size="small"
                    onClick={() => handleSelectAll(true)}
                    disabled={resultsCount === 0}
                  >
                    {selectAllText}
                  </Button>
                  <Button
                    size="small"
                    onClick={() => handleSelectAll(false)}
                    disabled={selectedCount === 0}
                  >
                    {unselectAllText}
                  </Button>
                </Space>
              )}
            </div>

            <div className="results-section">
              <div className="section-subtitle">
                搜索结果 ({resultsCount})
              </div>
              <Spin spinning={searchLoading} tip={searchLoadingText}>
                {renderResultsList()}
              </Spin>
            </div>
          </div>

          {/* 右侧：选中项目和表单 */}
          <div className="right-panel">
            <div className="selected-section">
              <div className="section-title selected-title">
                已选项目 {!isSingleSelect && `(${selectedCount})`}
              </div>
              
              <Spin spinning={bindLoading} tip={loadingText}>
                {renderSelectedList()}
              </Spin>
            </div>

            {/* 自定义内容 */}
            {renderCustomContent && renderCustomContent()}
            {/* 表单 */}
            {renderForm()}
          </div>
        </div>
      </Modal>
      
      {/* 性能监控面板 */}
      <PerformanceMonitor
        enabled={showPerformanceMonitor && visible}
        onMetricsChange={(metrics) => {
          // 可以在这里处理性能指标变化
          console.log('性能指标:', metrics)
        }}
      />
    </>
  )
})

// 设置组件显示名称
CommonBindModal.displayName = 'CommonBindModal'

export default CommonBindModal


